import {
  useRef,
  useState,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
// import { ContactInfo } from "../DealTracker/TradeFlowTrackerTabs/Tasks";
// import { Contact } from "../../types/TradeFlow";

type Props = {
  // contacts: Contact[];
  triggers: MentionTrigger[];
  placeholder?: string;
  id?: string;
  onInput?: (e: React.FormEvent<HTMLDivElement>) => void;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  disabled?: boolean;
};

export type MentionEditorHandle = {
  getValue: () => string;
  setValue: (value: string) => void;
  clear: () => void;
  focus: () => void;
};

export type MentionTrigger<T = any> = {
  char: string;
  getItems: (query: string) => T[];
  renderItem: (item: T) => React.ReactNode;
  insert: (item: T, range: Range) => void;
};

const MentionEditor = forwardRef<MentionEditorHandle, Props>((props, ref) => {
  const {
    //  contacts,
    triggers,
    id,
    onInput,
    onKeyDown,
    placeholder,
    className,
    disabled = false,
  } = props;

  const editorRef = useRef<HTMLDivElement>(null);
  const [showList, setShowList] = useState(false);
  // const [filtered, setFiltered] = useState<typeof contacts>([]);
  const [filtered, setFiltered] = useState<any[]>([]);

  const [query, setQuery] = useState("");
  const [dropdownAbove, setDropdownAbove] = useState(false);
  const [activeTrigger, setActiveTrigger] = useState<MentionTrigger | null>(
    null,
  );

  // expose ref methods
  useImperativeHandle(ref, () => ({
    getValue: () => {
      if (!editorRef.current) return "";

      const html = editorRef.current.innerHTML;

      if (html === "<br>" || html === "<div><br></div>") {
        return "";
      }

      return html;
    },

    setValue: (value: string) => {
      if (!editorRef.current) return;

      editorRef.current.innerHTML = value;

      // Move cursor to end
      const range = document.createRange();
      const selection = window.getSelection();

      range.selectNodeContents(editorRef.current);
      range.collapse(false);

      selection?.removeAllRanges();
      selection?.addRange(range);

      setShowList(false);
      setQuery("");
    },

    clear: () => {
      if (editorRef.current) editorRef.current.innerHTML = "";
      setShowList(false);
      setQuery("");
    },
    focus: () => editorRef.current?.focus(),
  }));

  const triggerMap = useMemo(
    () => Object.fromEntries(triggers.map((t) => [t.char, t])),
    [triggers],
  );

  const triggerChars = Object.keys(triggerMap);

  // helper: true caret offset inside the editor (counts characters across nodes)
  function getCaretCharacterOffsetWithin(element: HTMLElement) {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return 0;

    const range = sel.getRangeAt(0);
    const preRange = range.cloneRange();
    preRange.selectNodeContents(element);
    preRange.setEnd(range.endContainer, range.endOffset);
    return preRange.toString().length;
  }

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const editorEl = editorRef.current;
    if (!editorEl) return;

    if (onInput) onInput(e);

    const text = editorEl.innerText;
    const caretPos = getCaretCharacterOffsetWithin(editorEl);

    const upToCaret = text.slice(0, caretPos);

    let best: { trigger: MentionTrigger; index: number; query: string } | null =
      null;

    for (const char of triggerChars) {
      const index = upToCaret.lastIndexOf(char);
      if (index === -1) continue;

      const after = upToCaret.slice(index + 1);

      if (/\s/.test(after)) continue;
      if (!/^[\w-]*$/.test(after)) continue;

      if (!best || index > best.index) {
        best = {
          trigger: triggerMap[char],
          index,
          query: after,
        };
      }
    }

    if (!best) {
      setShowList(false);
      setActiveTrigger(null);
      return;
    }

    setActiveTrigger(best.trigger);
    setQuery(best.query);

    const result = best.trigger.getItems(best.query);

    setFiltered(result);

    // dropdown positioning
    const rect = editorEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    setDropdownAbove(spaceBelow < 150);

    setShowList(true);
  };

  const insertItem = (item: any) => {
    if (!activeTrigger) return;

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);

    // remove trigger + query
    range.setStart(range.endContainer, range.endOffset - (query.length + 1));
    range.deleteContents();

    activeTrigger.insert(item, range);

    // move caret after insertion
    sel.removeAllRanges();
    const newRange = document.createRange();
    newRange.selectNodeContents(editorRef.current!);
    newRange.collapse(false);
    sel.addRange(newRange);

    setShowList(false);
    setQuery("");
    setActiveTrigger(null);
  };

  return (
    <div className="relative w-full">
      <div
        ref={editorRef}
        contentEditable={!disabled}
        onInput={handleInput}
        className={`
            border rounded-lg p-2 text-sm min-h-[40px]
            max-h-40 overflow-y-auto
            outline-none
            ${disabled ? "bg-gray-100 cursor-not-allowed opacity-70" : "focus:ring-2 focus:ring-blue-300 focus:border-blue-300"}
            transition-all duration-200 ease-out
            ${className}
          `}
        data-placeholder={placeholder || "Type something..."}
        id={id}
        onKeyDown={onKeyDown}
      ></div>

      {showList && (
        <div
          className={`absolute w-64 bg-white border shadow rounded-lg z-20
              ${dropdownAbove ? "bottom-full mb-1" : "top-full mt-1"}
            `}
          style={{ maxHeight: "180px", overflowY: "auto" }}
        >
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">
              No suggestions available
            </div>
          ) : (
            filtered.map((item, i) => (
              <div
                key={i}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => insertItem(item)}
                className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
              >
                {activeTrigger?.renderItem(item)}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
});

export default MentionEditor;
